import { GcmDatePickerComponent, GcmLoadingService } from '@gcm/gcm-ui-angular';
import { Component, OnInit, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { ApiService } from '../core/http/api.service';
import { EnvironmentVariablesService } from '../services/environment-variables.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ContentfulService } from '../services/contentful.service';
import { SearchEngineService } from '../services/search-engine.service';
import { ActivityTrackingService } from '../services/activity-tracking.service';

import { NoAccessErrorComponent } from '../shared/no-access-error/no-access-error.component';
import { FormsModule } from '@angular/forms';
import { PortfolioDashboard, PortfolioDashboardPortfolioSeriesBalanceSummary } from '../models/classes/portfolio-dashboard.class';
import { PortfolioSummaryComponent } from '../shared/portfolio-summary/portfolio-summary.component';
import { DateFormatPipe } from '../pipes/date-format.pipe';
import { UserService } from '../services/user.service';
import { ErrorMessageService } from '../services/error-message.service';
import { BalanceSummary, UsdBalanceSummary, InvestorDashboardPortfolioSeriesBalanceSummary } from '../models/classes/investor-dashboard.class';

@Component({
  selector: 'app-portfolio-dashboard',
  imports: [
    NoAccessErrorComponent,
    FormsModule,
    PortfolioSummaryComponent,
    GcmDatePickerComponent,
    RouterModule
],
  templateUrl: './portfolio-dashboard.component.html',
  styleUrl: './portfolio-dashboard.component.scss'
})
export class PortfolioDashboardComponent implements OnInit {
  private contentfulService = inject(ContentfulService);
  private seoService = inject(SearchEngineService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private apiService = inject(ApiService);
  private loadingService = inject(GcmLoadingService);
  private evService = inject(EnvironmentVariablesService);
  private activityTracking = inject(ActivityTrackingService);
  private dateFormat = inject(DateFormatPipe);
  private errorMessageService = inject(ErrorMessageService);
  private userService = inject(UserService);

  readonly investorId = input<number | undefined>(undefined);
  readonly portfolioId = input<number | undefined>(undefined);
  errorMessage!: string;
  readonly iconSet = toSignal(
    this.contentfulService.getIconSetValue().pipe(filter((v): v is Record<string, string> => !!v)),
    { initialValue: undefined as unknown as Record<string, string> }
  );
  queryParams: Record<string, string> = {};
  showNoAccessError = false;
  reportingPeriod!: string;
  selectedAsOfDate!: string;
  asOfDropdownData: string[] = [];
  data!: PortfolioDashboard;

  ngOnInit(): void {
    this.showNoAccessError = false;
    this.seoService.setCanonicalURL();
    this.queryParams = this.activatedRoute.snapshot.queryParams;
    this.reportingPeriod = this.queryParams['reportingdate'];
    this.getPortfolioDashboardDetails(this.portfolioId(), this.reportingPeriod);
  }

  getAsOfDates() {
    this.asOfDropdownData = [];
    const asOfDateData = Object.keys(this.data.portfolioBalanceSummaries);
    const hidePriorToDate = new Date(this.evService.hideDatesPriorToDate)
    let formattedDateData: string[] = [];
    asOfDateData.forEach(date => {
      formattedDateData.push(this.dateFormat.transform(date, "MM/dd/yyyy") as string)
    })
    formattedDateData = formattedDateData.filter(d => new Date(d).getTime() >= hidePriorToDate.getTime()) //Remove dates prior to cutoff
    this.asOfDropdownData = formattedDateData.sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    if (this.reportingPeriod) {
      const formattedReportingPeriod = this.dateFormat.transform(this.reportingPeriod, "MM/dd/yyyy") as string
      if (this.reportingPeriod && this.asOfDropdownData.find(date => date === formattedReportingPeriod)) {
        this.selectedAsOfDate = formattedReportingPeriod;
      }
    }
    this.selectedAsOfDate ??= this.asOfDropdownData[0];
  }

  getPortfolioDashboardDetails(portfolioId: number | undefined, reportingPeriod: string) {
    (this.fetchDashboardDataFromApi(portfolioId, reportingPeriod) as Promise<PortfolioDashboard>).then((data: PortfolioDashboard) => {
      this.userService.setCookieValue('dashboardPortfolioId', (portfolioId as number).toString());
      this.addFlattenedBalancesToSummary(data);
      this.data = data;
      if (!this.reportingPeriod) {
        this.reportingPeriod = data?.summary?.reportingPeriod?.slice(0, 10);
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: {
            reportingdate: this.reportingPeriod
          },
          queryParamsHandling: 'merge'
        });
      }
      this.getAsOfDates();
    });
  }

  convert(entity: Record<string, BalanceSummary>): UsdBalanceSummary[]{
    return Object.entries(entity).map((x) => { return Object.assign(x[1], { reportingPeriod: x[0] }) }) as UsdBalanceSummary[];
  }

  convertPortfolioSeries(entity: Record<string, PortfolioDashboardPortfolioSeriesBalanceSummary>): InvestorDashboardPortfolioSeriesBalanceSummary[]{
    return Object.entries(entity).map((x) => { return Object.assign(x[1], { reportingPeriod: x[0] }) }) as InvestorDashboardPortfolioSeriesBalanceSummary[];
  }

  addFlattenedBalancesToSummary(portfolio: PortfolioDashboard) {
    portfolio.summary.portfolioBalanceSummary = this.convert(portfolio.portfolioBalanceSummaries).map(p => InvestorDashboardPortfolioSeriesBalanceSummary.fromUsdBalanceSummary(p));
    portfolio.summary.portfolioSeries.forEach(series => {
      series.portfolioSeriesBalanceSummary = this.convertPortfolioSeries(portfolio.portfolioSeriesBalanceSummaries[series.masterId]);
    });
  }

  fetchDashboardDataFromApi(portfolioId: number | undefined, reportingDate: string) {
    const loadingTask = 'fetchDashboardDataFromApi';
    this.loadingService.addAsyncTask(loadingTask);
    return new Promise((resolve, reject) => {
      this.apiService.getPortfolioDashboardDetails(Number(portfolioId), reportingDate).subscribe({
        next: (data) => {
          this.loadingService.resolveAsyncTask(loadingTask);
          resolve(data);
        },
        error: (err) => {
          this.loadingService.resolveAsyncTask(loadingTask);
          this.errorMessage = this.errorMessageService.getErrorMessage(err);
          reject(err);
        }
      });
    })
  }

  checkIfSeriesHasCommitments(series: Record<string, unknown>) {
    if ((series['portfolioSeriesBalanceSummary'] as unknown[])?.length > 0) {
      const currentPeriodSummary = (series['portfolioSeriesBalanceSummary'] as Record<string, unknown>[]).filter((s) => s['reportingPeriod'] == series['reportingPeriod'])
      series['hasCommitments'] = currentPeriodSummary[0]?.hasInvestmentCommitments;
    }
  }

  updateAsOfDate(date: string | string[] | Date | Date[] | (string | Date)[] | { start: string | Date; end: string | Date; type: string } | undefined) {
    if (!date || typeof date !== 'string') return;
    const formattedDate = this.dateFormat.transform(date, 'yyyy-MM-dd')
    this.activityTracking.toggledAsOfDate(date)
    if (date) {
      const reportingDate = (formattedDate as string).slice(0, 10);
      this.reportingPeriod = reportingDate;
      this.router.navigate(
        [], {
        relativeTo: this.activatedRoute,
        queryParams: { reportingdate: reportingDate },
        queryParamsHandling: 'merge'
      });
      this.getPortfolioDashboardDetails(this.portfolioId(), reportingDate);
    }
  }
}
